import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Sparkles, GraduationCap, Trophy, Lock, Star, Lightbulb, Bot, Cloud,
  Wand2, BarChart3, LineChart, Brain, Cpu,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getCertAccess } from '@/lib/certAccess';

const CERTS = [
  {
    code: 'gai-ess',
    name: 'Google AI Essentials',
    title: 'Google AI Essentials Certified',
    level: 'Essentials',
    levelColor: 'bg-sky-100 text-sky-700',
    accent: 'from-sky-500 to-cyan-600',
    icon: Sparkles,
    href: '/google-ai-essentials-exam-prep',
    price: 29,
    questions: 120,
    duration: '50 min',
    blurb: 'AI foundations, prompt engineering, productivity workflows, and responsible AI for every professional.',
    skills: ['AI foundations', 'Prompt engineering', 'AI productivity', 'Responsible AI', 'Career growth'],
  },
];

// Future roadmap (visual teaser only; not real cert pages yet)
const ROADMAP = [
  { name: 'Prompt Engineering',                  level: 'Specialization', icon: Wand2,      status: 'Coming Q2' },
  { name: 'Google Data Analytics',               level: 'Certificate',    icon: BarChart3,  status: 'Coming Q2' },
  { name: 'Google Advanced Data Analytics',      level: 'Certificate',    icon: LineChart,  status: 'Coming Q3' },
  { name: 'Machine Learning Engineer',           level: 'Professional',   icon: Brain,      status: 'Coming Q3' },
  { name: 'Generative AI on Google Cloud',       level: 'Specialization', icon: Cpu,        status: 'Coming Q4' },
];

export default function GoogleTrackPage() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState([]);
  const email = user?.email || (typeof window !== 'undefined' ? localStorage.getItem('paywall_email') : '');

  useEffect(() => {
    let cancelled = false;
    if (email) {
      getCertAccess(email).then((data) => {
        if (!cancelled) setUnlocked(data.unlocked || []);
      });
    }
    return () => { cancelled = true; };
  }, [email]);

  const owned = CERTS.filter((c) => unlocked.includes(c.code)).length;

  return (
    <div className="min-h-screen bg-white" data-testid="google-track-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-950 text-white pt-28 pb-20">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(56,189,248,0.4) 0px, transparent 50%), radial-gradient(circle at 85% 70%, rgba(34,211,238,0.35) 0px, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-white/10 text-white border-white/20 mb-6 hover:bg-white/15">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Google AI Track · AI-fluency for every professional
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            Become <span className="text-sky-300">AI-fluent</span> — the new
            <span className="text-cyan-300"> career superpower</span>, certified by Google.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Start with Google AI Essentials — the most-recommended cert for non-technical professionals adding generative AI
            to their workflow. Prep with 120 exam-grade questions, prompt engineering drills, and a 1:1 AI coach.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#track">
              <Button size="lg" className="bg-sky-400 hover:bg-sky-300 text-slate-950 font-semibold" data-testid="google-hero-view-track-btn">
                See the Google AI roadmap <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/google-ai-essentials-exam-prep">
              <Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/10" data-testid="google-hero-start-btn">
                Start free with AI Essentials
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl">
            {[
              { v: '1', l: 'Cert live (5 more coming)' },
              { v: '120+', l: 'Exam Questions' },
              { v: '$29', l: '2 months access' },
              { v: 'GPT-4o', l: 'AI Coach 1:1' },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                <div className="text-2xl font-bold">{s.v}</div>
                <div className="text-xs text-slate-300 uppercase tracking-wide">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRESS NUDGE */}
      {email && (
        <section className="border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-6 h-6 text-amber-500" />
              <div>
                <p className="text-sm text-slate-500">Signed in as <span className="font-medium text-slate-700">{email}</span></p>
                <p className="text-base font-semibold text-slate-900">
                  {owned} of {CERTS.length} Google certifications unlocked
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CERT CARDS */}
      <section id="track" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Badge variant="outline" className="mb-3"><GraduationCap className="w-3.5 h-3.5 mr-1" /> Available now</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Google AI Essentials — your launchpad</h2>
            <p className="mt-3 text-slate-600">
              The cert that gets every team — marketing, ops, sales, HR, finance — productive with AI tools, safely.
              Earned by Coursera; mastered with LearnQuanta.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CERTS.map((c) => {
              const isUnlocked = unlocked.includes(c.code);
              const Icon = c.icon;
              return (
                <Card key={c.code} className="border-slate-200 hover:shadow-xl transition-shadow" data-testid={`google-track-cert-card-${c.code}`}>
                  <div className={`h-1.5 bg-gradient-to-r ${c.accent} rounded-t-lg`} />
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <Badge className={c.levelColor}>{c.level}</Badge>
                    </div>
                    <CardTitle className="mt-4 text-xl">{c.name}</CardTitle>
                    <CardDescription>{c.title}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-4">{c.blurb}</p>
                    <ul className="space-y-1.5 mb-5">
                      {c.skills.slice(0, 4).map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> {s}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between mb-4 text-xs text-slate-500">
                      <span><Star className="w-3 h-3 inline mr-1" />{c.questions} questions</span>
                      <span>Sim: {c.duration}</span>
                    </div>
                    <Link to={c.href}>
                      <Button
                        className={`w-full ${isUnlocked ? 'bg-emerald-600 hover:bg-emerald-700' : `bg-gradient-to-r ${c.accent} hover:opacity-90`} text-white`}
                        data-testid={`google-track-cert-start-${c.code}`}
                      >
                        {isUnlocked ? (<><CheckCircle2 className="w-4 h-4 mr-2" /> Continue · ${c.price} unlocked</>) : (<>Start prep · ${c.price} <ArrowRight className="w-4 h-4 ml-2" /></>)}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <Badge variant="outline" className="mb-3"><Lock className="w-3.5 h-3.5 mr-1" /> Coming soon</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">The full Google certification ladder</h2>
            <p className="mt-3 text-slate-600">
              5 more programs on the way — from prompt engineering and data analytics all the way to ML Engineering and Generative AI on Google Cloud. Vote with your enrollments which one we should ship first.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROADMAP.map((r) => {
              const Icon = r.icon;
              return (
                <Card key={r.name} className="border-dashed border-slate-300 bg-white/60">
                  <CardContent className="p-5 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{r.level} · {r.status}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-600 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">Ready to become AI-fluent?</h2>
          <p className="mt-4 text-sky-50 text-lg">120 exam-grade questions · 50-minute simulation · AI Coach 1:1 · 2 months access — $29.</p>
          <Link to="/google-ai-essentials-exam-prep" className="inline-block mt-8">
            <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 font-semibold" data-testid="google-track-cta-btn">
              Start Google AI Essentials prep <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
